<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "../../PhotoBox.vue";

export default {
    name: "OrgChartTemp6",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: {
        orgChart: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
        startPage: { type: Number, default: 1 },
    },
    computed: {
        memberPages() {
            const pages = [];
            const members = this.orgChart.members || [];
            for (let i = 0; i < members.length; i += 4) {
                pages.push(members.slice(i, i + 4));
            }
            return pages.length > 0 ? pages : [[]];
        }
    }
};
</script>

<template>
    <div class="main-container" :style="{
        gap: pageGap ? '20px' : '0px',
        '--header-color': orgChart.style.colorScheme && orgChart.style.colorScheme.header,
        '--body-color': orgChart.style.colorScheme && orgChart.style.colorScheme.body,
        '--footer-color': orgChart.style.colorScheme && orgChart.style.colorScheme.footer,
        '--heading-color': orgChart.style.heading.color,
        '--heading-size': orgChart.style.heading.size + 'px',
        '--heading-weight': orgChart.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': orgChart.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': orgChart.style.heading.underline ? 'underline' : 'none',
        '--heading-font': orgChart.style.heading.font,
        '--label-color': orgChart.style.label.color,
        '--label-size': orgChart.style.label.size + 'px',
        '--label-weight': orgChart.style.label.bold ? 'bold' : 'normal',
        '--label-style': orgChart.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': orgChart.style.label.underline ? 'underline' : 'none',
        '--label-font': orgChart.style.label.font,
        '--text-color': orgChart.style.text.color,
        '--text-size': orgChart.style.text.size + 'px',
        '--text-weight': orgChart.style.text.bold ? 'bold' : 'normal',
        '--text-style': orgChart.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': orgChart.style.text.underline ? 'underline' : 'none',
        '--text-font': orgChart.style.text.font,
    }">
        <div v-for="(pageMembers, pageIndex) in memberPages" :key="pageIndex" class="profile-container page-unit">

            <!-- Header -->
            <div class="header">
                <h1 class="page-title">
                    <SpellCheckedText :value="orgChart.title || ''" @input="orgChart.title = $event" />
                </h1>
                <div class="logo-container photobox">
                    <PhotoBox :project="orgChart" type="logo" bgColor="transparent" objectFit="contain" />
                </div>
            </div>

            <!-- Content Area -->
            <div class="content-wrapper">
                <div class="content-area">
                    <div class="members-grid">
                        <div v-for="member in pageMembers" :key="member.teamMemberId" class="member-card">
                            <div class="image-container photobox">
                                <PhotoBox :project="member" type="profile" :bgColor="'#f0f0f0'" objectFit="cover" />
                            </div>
                            <div class="member-details">
                                <h2 class="member-name">
                                    <SpellCheckedText :value="member.teamMemberName || ''"
                                        @input="member.teamMemberName = $event" />
                                </h2>
                                <div class="role-section mb-3">
                                    <span class="label">Role on Project: </span>
                                    <span class="role-text">
                                        <SpellCheckedText :value="member.role || ''" @input="member.role = $event" />
                                    </span>
                                </div>
                                <div class="bio-section">
                                    <span class="label">Bio: </span>
                                    <span class="bio-text">
                                        <SpellCheckedText :value="member.whyTeamMember || ''"
                                            @input="member.whyTeamMember = $event" />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="footer">
                <div class="footer-left">
                    <SpellCheckedText :value="orgChart.opprName || ''" @input="orgChart.opprName = $event" />
                </div>
                <div class="footer-right">
                    <span class="company-name">
                        <SpellCheckedText :value="orgChart.submittedBy || ''" @input="orgChart.submittedBy = $event" />
                    </span>
                    <!-- <span class="divider">|</span> -->
                    <span class="page-number">{{ String(startPage + pageIndex).padStart(2, '0') }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: flex;
    flex-direction: column;
    font-family: 'Arial', sans-serif;

    .profile-container {
        width: 297mm;
        height: 210mm;
        display: flex;
        flex-direction: column;
        background: white;
        box-sizing: border-box;
        overflow: hidden;
        position: relative;
    }

    &:hover {
        .photobox :deep(.photo-overlay) {
            opacity: 1;
        }

        .photobox :deep(img) {
            opacity: 0.9;
        }
    }

    .header {
        height: 80px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 20px;

        .page-title {
            color: var(--header-color);
            font-size: 28px;
            font-family: Helvetica;
            font-weight: bold;
            margin: 0;
        }

        .logo-container {
            height: 70px;
            width: 180px;
        }
    }

    .content-wrapper {
        display: flex;
        flex: 1;
        background: var(--body-color);
        padding: 40px;

        .content-area {
            height: 100%;

            .members-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                grid-template-rows: 1fr 1fr;
                gap: 40px;
            }

            .member-card {
                display: flex;
                align-items: flex-start;
                gap: 20px;

                .image-container {
                    flex-shrink: 0;
                    width: 150px;
                    height: 150px;
                    background: #f0f0f0;
                    border-radius: 50%;
                    overflow: hidden;

                    :deep(img) {
                        width: 100%;
                        height: 100%;
                        border-radius: 50%;
                        object-fit: cover;
                    }

                    :deep(.photo-box-wrapper),
                    :deep(.image-container) {
                        width: 100%;
                        height: 100%;
                    }
                }

                .member-details {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    max-height: 250px;
                    overflow: hidden;

                    .member-name {
                        color: var(--heading-color);
                        font-size: var(--heading-size);
                        font-family: var(--heading-font);
                        font-weight: var(--heading-weight);
                        font-style: var(--heading-style);
                        text-decoration: var(--heading-decoration);
                        margin: 0;
                    }

                    .role-section,
                    .bio-section {
                        .label {
                            color: var(--label-color);
                            font-size: var(--label-size);
                            font-family: var(--label-font);
                            font-weight: var(--label-weight);
                            font-style: var(--label-style);
                            text-decoration: var(--label-decoration);
                            margin: 0
                        }

                        .role-text,
                        .bio-text {
                            color: var(--text-color);
                            font-size: var(--text-size);
                            font-family: var(--text-font);
                            font-weight: var(--text-weight);
                            font-style: var(--text-style);
                            text-decoration: var(--text-decoration);
                            margin: 0;
                        }

                        .bio-text {
                            overflow: hidden;
                        }
                    }
                }
            }
        }
    }

    .footer {
        height: 80px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 40px;
        font-size: 12px;
        padding-top: 10px;

        .footer-left {
            font-size: 12px;
            color: var(--footer-color);
            font-weight: bold;
            margin: 0;
        }

        .footer-right {
            display: flex;
            align-items: center;

            .company-name {
                font-size: 12px;
                color: var(--footer-color);
                font-weight: bold;
                margin: 0;
            }

            .divider {
                font-size: 12px;
                color: var(--footer-color);
                font-weight: bold;
                margin: 0;
                padding: 0px 10px;
            }

            .page-number {
                font-size: 16px;
                color: var(--header-color);
                font-weight: 800;
                margin: 0;
            }
        }
    }
}
</style>
